import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChild,
  contentChildren,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  TemplateRef,
  Type,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { menuItems, routeFragment } from '@softline/application';
import { PageHeaderComponent } from './page-header/page-header.component';
import { MenuOutletComponent } from '../../menu/menu-outlet/menu-outlet.component';
import { PageTabComponent } from './page-tab/page-tab.component';
import { isDefined, isDefinedNotEmpty } from '@softline/core';
import { PageSettingsService } from '../../user/page-settings/page-settings.service';
import { InstanceofPipe, ObserveElementSizeDirective } from '@softline/ui-core';
import { ShellService } from '../../shell/services/shell.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { Router } from '@angular/router';
import {
  injectShellEnvironment,
  isEmbeddedShellEnvironment,
} from '../../shell/shell.tokens';

@Component({
  selector: 'soft-page',
  templateUrl: './page.component.html',
  styleUrl: './page.component.css',
  imports: [
    CommonModule,
    MenuOutletComponent,
    ObserveElementSizeDirective,
    InstanceofPipe,
    CdkScrollable,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // different margin/height whether in default or in embedded shell
    '[style.margin]': 'environmentAwareMargin()',
    '[style.height]': 'environmentAwareHeight()',
  },
})
export class PageComponent implements AfterContentInit {
  readonly shellEnvironment = injectShellEnvironment();

  private NAME = 'page';
  readonly service = inject(PageSettingsService, { optional: true });
  readonly shellService = inject(ShellService);
  readonly router = inject(Router);
  readonly routeFragment = routeFragment();

  public TemplateRef = TemplateRef;

  readonly environmentAwareMargin = computed(() => {
    return isEmbeddedShellEnvironment(this.shellEnvironment)
      ? '0px'
      : '-8px -16px 0 -16px';
  });

  readonly environmentAwareHeight = computed(() => {
    return isEmbeddedShellEnvironment(this.shellEnvironment)
      ? '100%'
      : 'calc(100% + 8px)';
  });

  blockScrollY = input(false);
  customClass = input('');

  bottomContent = input<TemplateRef<any> | Type<any>>();
  observeContentSize = input(true);

  header = contentChild(PageHeaderComponent, { descendants: true });
  selectedTabName = signal<string | null>(null);

  tabs = contentChildren(PageTabComponent, { descendants: true });

  mainContent = viewChild('main', { read: ElementRef });

  selectedTabTemplate = computed(() => {
    const tabs = this.tabs();
    const selectedTabName = this.selectedTabName();
    const selectedTab = tabs.find((tab) => tab.name() === selectedTabName) ?? tabs[0];
    if (isDefined(selectedTab))
      return selectedTab.contentTemplate() ?? selectedTab.template();
    return null;
  });

  menuItems = menuItems(() => {
    const tabs = this.tabs();
    return tabs.map((tab) => ({
      name: tab.name() ?? '',
      type: 'route',
      icon: tab.icon() ?? '',
      title: tab.title() ?? '',
      class: tab.class() ?? '',
      outlet: 'tab',
      routerLink: [],
      routerLinkParams: {
        fragment: tab.name(),
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      },
    }));
  });

  fragmentEffect = effect(() => {
    const fragment = this.routeFragment();
    const selectedTabName = untracked(() => this.selectedTabName());
    if (isDefinedNotEmpty(fragment) && fragment !== selectedTabName)
      untracked(() => this.selectedTabName.set(fragment));
  });

  selectedTabNameEffect = effect(() => {
    const selectedTabName = this.selectedTabName();
    const service = this.service;
    if (service && isDefined(selectedTabName))
      untracked(() =>
        service.set(this.NAME, { name: this.NAME, settings: { selectedTabName } })
      );
  });

  constructor(private cdRef: ChangeDetectorRef) {}

  ngAfterContentInit() {
    const fragment = this.routeFragment();
    const tabs = this.tabs();

    if (isDefinedNotEmpty(fragment)) {
      this.selectedTabName.set(fragment);
      this.cdRef.markForCheck();
    } else if ((tabs?.length || 0) > 0) {
      let tabName = tabs[0].name();
      if (this.service) {
        const settings = this.service
          .pageSettings(this.NAME)()
          .components.find((o) => o.name === this.NAME)?.settings;
        if (settings && isDefined(settings['selectedTabName'])) {
          tabName = settings['selectedTabName'] as string;
        }
      }
      this.router.navigate([], {
        fragment: tabName,
        queryParamsHandling: 'preserve',
        replaceUrl: true,
      });
      this.cdRef.markForCheck();
    }

    const mainContent = this.mainContent();

    if (mainContent) {
      this.contentSizeChanged(mainContent.nativeElement.getBoundingClientRect());
    }
  }

  contentSizeChanged(rect: DOMRect): void {
    if (this.observeContentSize()) {
      this.shellService.setContentRect(rect);
    }
  }
}
